import React, { useEffect, useState } from "react";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { ShieldCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { log } from "../lib/log";

/**
 * Two-factor authentication settings — TOTP enrol / disable.
 *
 * Self-contained drop-in component.  Renders three states:
 *   1. enabled:false, pending:false  → "Enable 2FA" button.
 *   2. pending:true                  → QR/secret + 6-digit code input.
 *   3. enabled:true                  → "Disable 2FA" form (password + code).
 */
export default function TwoFactorSettings() {
  const { lang } = useI18n();
  const { user } = useAuth();
  const isAr = lang === "ar";

  const [status, setStatus] = useState({ enabled: false, pending: false });
  const [loading, setLoading] = useState(true);
  const [setup, setSetup] = useState(null); // { otpauth_url, secret }
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  // Disable form
  const [pwd, setPwd] = useState("");
  const [disableCode, setDisableCode] = useState("");

  const t = {
    title: isAr ? "المصادقة الثنائية (2FA)" : "Two-Factor Authentication",
    lead: isAr
      ? "احمِ حسابك بطبقة إضافية. عند تفعيلها ستحتاج رمزاً مكوّناً من 6 أرقام من تطبيق المصادقة عند كل تسجيل دخول."
      : "Add a second layer of protection. Once enabled, you'll need a 6-digit code from your authenticator app at every login.",
    enable: isAr ? "تفعيل المصادقة الثنائية" : "Enable 2FA",
    enableHint: isAr
      ? "افتح تطبيق المصادقة (Google Authenticator، Authy، 1Password) وامسح الـ QR، أو أدخل المفتاح يدوياً، ثم أدخل الرمز."
      : "Open your authenticator (Google Authenticator, Authy, 1Password) and scan the QR or enter the key manually, then submit the 6-digit code.",
    secret: isAr ? "المفتاح اليدوي" : "Manual key",
    code: isAr ? "الرمز المكوّن من 6 أرقام" : "6-digit code",
    activate: isAr ? "تفعيل" : "Activate",
    cancel: isAr ? "إلغاء" : "Cancel",
    enabled: isAr ? "✓ مفعّلة" : "✓ Enabled",
    disableTitle: isAr ? "إيقاف المصادقة الثنائية" : "Disable 2FA",
    pwd: isAr ? "كلمة المرور الحالية" : "Current password",
    disable: isAr ? "إيقاف" : "Disable",
    err: isAr ? "حدث خطأ. حاول مرة أخرى." : "Something went wrong.",
    enableOk: isAr ? "تم تفعيل المصادقة الثنائية." : "Two-factor authentication enabled.",
    disableOk: isAr ? "تم إيقاف المصادقة الثنائية." : "Two-factor authentication disabled.",
  };

  const loadStatus = async () => {
    try {
      const { data } = await api.get("/auth/2fa/status");
      setStatus(data);
    } catch (err) {
      // 2FA status is a read-only sidebar; log silently and keep
      // defaults so the rest of the page stays usable.
      // eslint-disable-next-line no-console
      log.error("2fa status fetch failed:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) loadStatus();
  }, [user]);  // eslint-disable-line react-hooks/exhaustive-deps

  const startSetup = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/setup");
      setSetup(data);
      setStatus({ ...status, pending: true });
    } catch {
      toast.error(t.err);
    } finally {
      setBusy(false);
    }
  };

  const finishSetup = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/2fa/enable", { code });
      toast.success(t.enableOk);
      setSetup(null);
      setCode("");
      await loadStatus();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t.err);
    } finally {
      setBusy(false);
    }
  };

  const onDisable = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/2fa/disable", {
        password: pwd,
        code: disableCode || undefined,
      });
      toast.success(t.disableOk);
      setPwd("");
      setDisableCode("");
      await loadStatus();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t.err);
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="border border-slate-200 p-6">
        <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
      </div>
    );
  }

  // QR is rendered server-free using Google Charts API. Free + no extra
  // bundle weight; the secret is the same we already stored, no leak.
  const qrSrc = setup
    ? `https://api.qrserver.com/v1/create-qr-code/?size=240x240&data=${encodeURIComponent(setup.otpauth_url)}`
    : null;

  return (
    <div className="border border-slate-200 p-6 lg:p-8" data-testid="2fa-settings">
      <div className="flex items-start gap-3 mb-4">
        <ShieldCheck className="w-5 h-5 text-slate-700 shrink-0 mt-1" />
        <div>
          <h3 className="font-display text-2xl tracking-tight">{t.title}</h3>
          <p className="text-sm text-slate-600 mt-1 leading-relaxed">{t.lead}</p>
        </div>
      </div>

      {/* state 1 — not enabled, no setup in progress */}
      {!status.enabled && !setup && (
        <Button
          type="button"
          onClick={startSetup}
          disabled={busy}
          data-testid="2fa-start"
          className="rounded-sm h-11 bg-slate-900 hover:bg-slate-800 text-white"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : t.enable}
        </Button>
      )}

      {/* state 2 — pending: scan + verify */}
      {!status.enabled && setup && (
        <form onSubmit={finishSetup} className="space-y-4 mt-2" data-testid="2fa-verify-form">
          <p className="text-sm text-slate-600">{t.enableHint}</p>
          <img
            src={qrSrc}
            alt="2FA QR code"
            data-testid="2fa-qr"
            className="border border-slate-200"
          />
          <div className="text-xs text-slate-500">
            {t.secret}:{" "}
            <code className="bg-slate-50 px-2 py-1 select-all" data-testid="2fa-secret">
              {setup.secret}
            </code>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">
              {t.code}
            </Label>
            <Input
              type="text"
              inputMode="numeric"
              pattern="[0-9]{6}"
              maxLength={6}
              required
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              data-testid="2fa-code-input"
              className="mt-2 rounded-sm h-11 tracking-[0.5em] text-center font-mono"
            />
          </div>
          <div className="flex gap-2">
            <Button
              type="submit"
              disabled={busy || code.length !== 6}
              data-testid="2fa-activate"
              className="rounded-sm h-11 bg-slate-900 hover:bg-slate-800 text-white"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : t.activate}
            </Button>
            <Button
              type="button"
              onClick={() => { setSetup(null); setCode(""); }}
              variant="outline"
              className="rounded-sm h-11"
            >
              {t.cancel}
            </Button>
          </div>
        </form>
      )}

      {/* state 3 — already enabled, allow disable */}
      {status.enabled && (
        <form onSubmit={onDisable} className="space-y-4 mt-2" data-testid="2fa-disable-form">
          <p className="text-sm font-medium text-emerald-700" data-testid="2fa-status-enabled">
            {t.enabled}
          </p>
          <h4 className="font-medium pt-2 border-t border-slate-100">
            {t.disableTitle}
          </h4>
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">{t.pwd}</Label>
            <Input
              type="password"
              required
              value={pwd}
              onChange={(e) => setPwd(e.target.value)}
              data-testid="2fa-disable-pwd"
              className="mt-2 rounded-sm h-11"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">{t.code}</Label>
            <Input
              type="text"
              inputMode="numeric"
              pattern="[0-9]{6}"
              maxLength={6}
              value={disableCode}
              onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, ""))}
              data-testid="2fa-disable-code"
              className="mt-2 rounded-sm h-11 tracking-[0.5em] text-center font-mono"
            />
          </div>
          <Button
            type="submit"
            disabled={busy || !pwd}
            data-testid="2fa-disable-submit"
            variant="destructive"
            className="rounded-sm h-11"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : t.disable}
          </Button>
        </form>
      )}
    </div>
  );
}
